<template>
  <div v-if="visible" :class="[
    'flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow-sm dark:text-gray-400 dark:bg-gray-800 z-50 transition-opacity',
    fadeClass
  ]" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" role="alert">
    <!-- Success Icon -->
    <CheckCircleIcon v-if="type === 'success'" :class="typeClass" />
    <!-- Error Icon -->
    <XCircleIcon v-if="type === 'error'" :class="typeClass" />
    <!-- Warning Icon -->
    <ExclamationCircleIcon v-if="type === 'warning'" :class="typeClass" />

    <div class="ms-3 text-sm font-normal">{{ message }}</div>
    <button @click="close" type="button"
      class="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700">
      <span class="sr-only">Close</span>
      <XIcon class="w-5 h-5" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import CheckCircleIcon from '~/components/icons/icon-check-circle.vue';
import XCircleIcon from '~/components/icons/icon-x-circle.vue';
import ExclamationCircleIcon from '~/components/icons/icon-exclamation-circle.vue';
import XIcon from '~/components/icons/icon-x.vue';

const props = defineProps({
  message: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'success',
    validator: (value: string) => ['success', 'error', 'warning'].includes(value)
  },
  duration: {
    type: Number,
    default: 5000
  },
  position: {
    type: String,
    default: 'top-right',
    validator: (value: string) => ['top-right', 'top-left', 'bottom-right', 'bottom-left', 'top-center', 'bottom-center'].includes(value)
  },
  pauseOnHover: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['close']);
const visible = ref(false);
const fading = ref(false);
const timer = ref<number | null>(null);

const typeClass = computed(() => {
  switch (props.type) {
    case 'success':
      return 'text-teal-500';
    case 'error':
      return 'text-red-500';
    case 'warning':
      return 'text-orange-500';
    default:
      return 'text-teal-500';
  }
});

const fadeClass = computed(() => {
  return fading.value ? 'opacity-0' : 'opacity-100';
});

const close = () => {
  fading.value = true;
  setTimeout(() => {
    visible.value = false;
    emit('close');
  }, 300);
};

const startTimer = () => {
  if (props.duration > 0) {
    timer.value = window.setTimeout(() => {
      close();
    }, props.duration);
  }
};

const clearTimer = () => {
  if (timer.value) {
    clearTimeout(timer.value);
    timer.value = null;
  }
};

const handleMouseEnter = () => {
  if (props.pauseOnHover) {
    clearTimer();
  }
};

const handleMouseLeave = () => {
  if (props.pauseOnHover) {
    startTimer();
  }
};

onMounted(() => {
  visible.value = true;
  startTimer();
});

onBeforeUnmount(() => {
  clearTimer();
});

watch(() => props.message, () => {
  clearTimer();
  visible.value = true;
  fading.value = false;
  startTimer();
});
</script>

<style scoped>
.transition-opacity {
  transition: opacity 0.3s ease;
}
</style>